import { getToken } from '../../Utils/authUtils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { DASHBOARD } from '../../constant';

/**
 * What the clinic is charged for.
 *
 * `per_study` / `per_report` multiply the rate by a volume; `monthly` is a flat
 * subscription for the period and ignores volume entirely. The server rejects a
 * `monthly` basis paired with a weekly or fortnightly invoice frequency, since that
 * would charge the whole monthly fee two or four times a month.
 */
export type BillingBasis = 'per_study' | 'per_report' | 'monthly';

type Args = {
  clinicId: string;
  basis: BillingBasis;
  rate: number;
  currency?: string;
  paymentTermsDays?: number;
  /**
   * How the due date is derived. `days` adds paymentTermsDays to the issue date;
   * `end_of_month` falls due on the last day of the month the invoice was RAISED in,
   * which keeps a clinic's invoices inside one monthly payment run.
   */
  paymentTermsMode?: 'days' | 'end_of_month';
  /** The clinic's ABN or ACN, printed under "Billed to". Free text — an ACN is 9 digits. */
  abn?: string;
  billingEmail?: string;
  autoInvoice?: {
    enabled: boolean;
    frequency: 'weekly' | 'fortnightly' | 'monthly';
    anchorDay: number;
    autoSend: boolean;
  };
};

/**
 * Save a clinic's billing rate. Super admin only — the server re-checks the caller's
 * type, so this is not the security boundary, just the call.
 */
export const updateClinicBilling = createAsyncThunk(
  'dashboard/updateClinicBilling',
  async (arg: Args, { rejectWithValue }) => {
    try {
      const res = await fetch(DASHBOARD.UPDATE_CLINIC_BILLING, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + getToken(),
        },
        body: JSON.stringify(arg),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        return rejectWithValue(errorData.msg || 'Failed to update billing');
      }
      return await res.json();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
